import { useEffect, useState, type ReactNode } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { BootSteps } from '@marcianus/design/components';
import { getLoadingContext } from '@/api/loading';
import { getDashboard } from '@/api/admin';
import { listContent, getStudioStatus } from '@/api/studio';
import {
  listOrgs, listProducts, getFiscal, getBiEmbed, listConversations,
} from '@/api/console';
import { apiGet } from '@/api/http';
import { useTranslation } from 'react-i18next';

/**
 * BootGate — a consola carrega TUDO de uma vez, atrás do splash MDS.
 *
 * Padrão Linear/Superhuman: paga-se o custo uma vez no arranque e depois a
 * navegação é instantânea (as páginas encontram as queries já quentes no
 * cache). Promise.allSettled — uma fonte falhada não bloqueia a consola;
 * a página respetiva mostra o seu próprio erro. Teto de 6s: o splash nunca
 * prende ninguém (o que faltar continua a carregar por trás).
 */
const MIN_SPLASH_MS = 450;   // sem flash: o splash não pisca em ligações rápidas
const MAX_SPLASH_MS = 6000;  // teto duro: nunca prender a equipa no arranque

export function BootGate({ children }: { children: ReactNode }) {
  const { t } = useTranslation('hq');
  const qc = useQueryClient();
  const [ready, setReady] = useState(false);

  // Sonda de arranque — em paralelo com o warmup, sem o bloquear e sem `retry`.
  const { data: boot } = useQuery({
    queryKey: ['loading', 'boot'],
    queryFn: () => getLoadingContext('boot'),
    staleTime: 30_000,
    retry: false,
  });

  useEffect(() => {
    let alive = true;
    const started = Date.now();
    // As chaves têm de ser EXATAMENTE as das páginas — é isso que torna a
    // navegação instantânea (cache hit) em vez de um segundo fetch.
    const warmup = Promise.allSettled([
      qc.prefetchQuery({ queryKey: ['dashboard'], queryFn: getDashboard }),
      qc.prefetchQuery({ queryKey: ['admin-orgs'], queryFn: listOrgs }),
      qc.prefetchQuery({ queryKey: ['admin-marketplace'], queryFn: listProducts }),
      qc.prefetchQuery({ queryKey: ['cms', 'content'], queryFn: listContent }),
      qc.prefetchQuery({ queryKey: ['studio', 'status'], queryFn: getStudioStatus }),
      qc.prefetchQuery({ queryKey: ['admin-fiscal'], queryFn: getFiscal }),
      qc.prefetchQuery({ queryKey: ['admin-audit', ''], queryFn: () => apiGet('/admin/audit?limit=200') }),
      qc.prefetchQuery({ queryKey: ['bi-embed', 2, 'light'], queryFn: () => getBiEmbed(2, 'light') }),
      qc.prefetchQuery({ queryKey: ['support-conversations', 'open'], queryFn: () => listConversations('open') }),
    ]);
    const timeout = new Promise((r) => setTimeout(r, MAX_SPLASH_MS));
    Promise.race([warmup, timeout]).then(() => {
      const wait = Math.max(0, MIN_SPLASH_MS - (Date.now() - started));
      setTimeout(() => { if (alive) setReady(true); }, wait);
    });
    return () => { alive = false; };
  }, [qc]);

  if (!ready) {
    return (
      <div className="boot-splash" role="status" aria-label={t('boot.loadingConsole')}>
        <img className="boot-logo" src="/symbol.png" alt="Marcianus" />
        <div className="boot-text">
          <span className="eyebrow">Marcianus HQ</span>
          <span className="muted small">{t('boot.loadingOperation')}</span>
        </div>
        {/* Os MESMOS passos que a app lean mostra — é o mesmo backend a
            arrancar. Quem opera a consola vê o estado real do sistema antes
            sequer de entrar, em vez de uma frase que diz sempre o mesmo. */}
        <BootSteps steps={boot?.steps ?? []} />
      </div>
    );
  }
  return <>{children}</>;
}
